import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, ArrowLeft, ArrowRight, Loader2, Upload, FileText, X } from 'lucide-react';
import { JobsShell } from '../components/jobs/JobsShell';
import { useJobseekerAuth, formatApiError } from '../contexts/JobseekerAuthContext';

const ROLES = [
  'Dental Assistant',
  'Registered Dental Assistant',
  'Dental Hygienist',
  'Office Manager',
  'Front Desk',
  'Treatment Coordinator',
  'Billing Specialist',
  'Orthodontic Assistant',
  'Oral Surgery Assistant',
  'Pediatric Dental Assistant',
  'Sterilization Technician',
];

const AVAILABILITIES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'per_diem', label: 'Per-diem / Temp' },
  { value: 'any', label: 'Open to anything' },
];

export default function JobsSignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, apiRequest } = useJobseekerAuth();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');

  // Step 1: account
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Step 2: preferences
  const [phone, setPhone] = useState('');
  const [zipCode, setZipCode] = useState(searchParams.get('zip') || '');
  const [rolesInterested, setRolesInterested] = useState([]);
  const [yearsExperience, setYearsExperience] = useState('');
  const [availability, setAvailability] = useState('any');
  const [desiredPayMin, setDesiredPayMin] = useState('');

  const pwStrong = useMemo(() => {
    return password.length >= 8 && /[A-Za-z]/.test(password) && /[0-9]/.test(password);
  }, [password]);

  const step1Valid = email && pwStrong && firstName.trim() && lastName.trim();
  const step2Valid = true; // optional fields

  const toggleRole = (r) => {
    setRolesInterested((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };

  const handleResumePick = (e) => {
    setResumeError('');
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      setResumeError('Please choose a PDF or DOCX file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setResumeError('File is too large. Max size is 5 MB.');
      return;
    }
    setResumeFile(file);
  };

  const handlePhotoPick = (e) => {
    setPhotoError('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoError('Please choose a JPG, PNG, or WEBP image.');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setPhotoError('Photo is too large. Max size is 3 MB.');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!step1Valid) {
      setStep(1);
      setError('Please complete the account fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // 1. Create the account
      await signup({
        email: email.trim().toLowerCase(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || null,
        zipCode: zipCode.trim() || null,
        rolesInterested,
        yearsExperience: yearsExperience ? Number(yearsExperience) : null,
        availability,
        desiredPayMin: desiredPayMin ? Number(desiredPayMin) : null,
      });

      // 2. Upload resume if provided (token is already saved by signup())
      if (resumeFile) {
        const fd = new FormData();
        fd.append('file', resumeFile);
        try {
          await apiRequest('post', '/api/jobs/me/resume', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (uploadErr) {
          console.warn('Resume upload failed:', uploadErr);
        }
      }

      // 3. Upload headshot if provided
      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        try {
          await apiRequest('post', '/api/jobs/me/photo', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (uploadErr) {
          console.warn('Photo upload failed:', uploadErr);
        }
      }

      navigate('/jobs/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <JobsShell showSignupLink={false}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="jobs-signup-page">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Stepper */}
          <div className="px-6 sm:px-8 pt-6">
            <div className="flex items-center justify-center gap-2 sm:gap-4">
              {[1, 2, 3].map((n) => (
                <React.Fragment key={n}>
                  <div className={`flex items-center gap-2 ${step === n ? 'text-blue-700' : step > n ? 'text-emerald-600' : 'text-slate-400'}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                      step === n ? 'bg-blue-700 text-white' :
                      step > n ? 'bg-emerald-600 text-white' :
                      'bg-slate-200 text-slate-500'
                    }`}>
                      {step > n ? <CheckCircle2 className="w-5 h-5" /> : n}
                    </div>
                    <span className="hidden sm:inline text-sm font-semibold">
                      {n === 1 ? 'Account' : n === 2 ? 'Profile' : 'Resume'}
                    </span>
                  </div>
                  {n < 3 && <div className={`h-px w-8 sm:w-16 ${step > n ? 'bg-emerald-600' : 'bg-slate-300'}`} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="px-6 sm:px-10 py-8">
            {error && (
              <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg" data-testid="signup-error">
                {error}
              </div>
            )}

            {step === 1 && (
              <div data-testid="signup-step-1">
                <h2 className="text-2xl font-extrabold text-slate-900">Create your free account</h2>
                <p className="text-slate-600 mt-1 text-sm">Step 1 of 3. Takes less than 60 seconds.</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First name *">
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="input-base"
                      data-testid="signup-first-name"
                      autoFocus
                    />
                  </Field>
                  <Field label="Last name *">
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="input-base"
                      data-testid="signup-last-name"
                    />
                  </Field>
                  <Field label="Email *" className="sm:col-span-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-base"
                      data-testid="signup-email"
                    />
                  </Field>
                  <Field label="Password *" className="sm:col-span-2"
                         hint="At least 8 characters, with both letters and numbers.">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-base"
                      data-testid="signup-password"
                    />
                    {password && !pwStrong && (
                      <p className="text-xs text-amber-700 mt-1">
                        Password must be at least 8 characters and include both letters and numbers.
                      </p>
                    )}
                  </Field>
                </div>
                <div className="mt-8 flex justify-between items-center">
                  <Link to="/jobs" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" /> Back to home
                  </Link>
                  <button
                    disabled={!step1Valid}
                    onClick={() => setStep(2)}
                    data-testid="signup-next-step-1"
                    className="px-6 py-3 bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 text-white font-bold rounded-lg shadow-md flex items-center gap-2 transition-colors"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <p className="mt-4 text-xs text-slate-500 text-center">
                  Already have an account?{' '}
                  <Link to="/jobs/login" className="text-blue-700 font-semibold hover:underline" data-testid="signup-to-login-link">
                    Sign in
                  </Link>
                </p>
              </div>
            )}

            {step === 2 && (
              <div data-testid="signup-step-2">
                <h2 className="text-2xl font-extrabold text-slate-900">Tell us what you're looking for</h2>
                <p className="text-slate-600 mt-1 text-sm">Step 2 of 3. Optional — but it helps us match you to the right jobs.</p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Phone (optional)">
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="input-base" data-testid="signup-phone" />
                  </Field>
                  <Field label="ZIP code" hint="So we can match you with nearby practices.">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ''))}
                      className="input-base"
                      data-testid="signup-zip"
                    />
                  </Field>
                  <Field label="Years of experience" className="sm:col-span-1">
                    <select
                      value={yearsExperience}
                      onChange={(e) => setYearsExperience(e.target.value)}
                      className="input-base"
                      data-testid="signup-years"
                    >
                      <option value="">Select…</option>
                      <option value="0">Less than 1 year</option>
                      <option value="1">1 year</option>
                      <option value="2">2 years</option>
                      <option value="3">3 years</option>
                      <option value="4">4 years</option>
                      <option value="5">5 years</option>
                      <option value="7">6–7 years</option>
                      <option value="9">8–10 years</option>
                      <option value="13">11–15 years</option>
                      <option value="18">16–20 years</option>
                      <option value="25">20+ years</option>
                    </select>
                  </Field>
                  <Field label="Desired hourly rate (USD)" hint="Optional — we never share this with practices automatically.">
                    <input type="number" min="0" step="1" value={desiredPayMin} onChange={(e) => setDesiredPayMin(e.target.value)} className="input-base" data-testid="signup-pay" placeholder="e.g. 28" />
                  </Field>
                  <Field label="Availability" className="sm:col-span-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {AVAILABILITIES.map((a) => (
                        <button
                          key={a.value}
                          type="button"
                          onClick={() => setAvailability(a.value)}
                          data-testid={`signup-avail-${a.value}`}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                            availability === a.value
                              ? 'bg-blue-50 border-blue-600 text-blue-700'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                          }`}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Roles you're interested in" className="sm:col-span-2">
                    <div className="flex flex-wrap gap-2">
                      {ROLES.map((r) => {
                        const on = rolesInterested.includes(r);
                        return (
                          <button
                            key={r}
                            type="button"
                            onClick={() => toggleRole(r)}
                            data-testid={`signup-role-${r.replace(/\s+/g, '-').toLowerCase()}`}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
                              on
                                ? 'bg-cyan-50 border-cyan-500 text-cyan-800'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                            }`}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </div>
                <div className="mt-8 flex justify-between">
                  <button onClick={() => setStep(1)} className="px-4 py-2.5 text-slate-700 hover:bg-slate-100 rounded-lg font-semibold flex items-center gap-2" data-testid="signup-back-step-2">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button onClick={() => setStep(3)} className="px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-lg shadow-md flex items-center gap-2" data-testid="signup-next-step-2">
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="signup-step-3">
                <h2 className="text-2xl font-extrabold text-slate-900">Add your resume &amp; photo</h2>
                <p className="text-slate-600 mt-1 text-sm">Step 3 of 3. A professional headshot helps practices remember you.</p>

                {/* Photo */}
                <div className="mt-6">
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-2">
                    Headshot photo <span className="text-slate-400 font-normal">(JPG/PNG/WEBP, up to 3&nbsp;MB)</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl text-slate-400">📷</span>
                      )}
                    </div>
                    <label
                      htmlFor="photo-upload"
                      className="px-4 py-2 bg-white border-2 border-blue-300 hover:border-blue-500 text-blue-700 font-bold text-sm rounded-lg cursor-pointer"
                      data-testid="photo-pick-btn"
                    >
                      {photoFile ? 'Replace photo' : 'Choose photo'}
                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handlePhotoPick}
                        className="hidden"
                        data-testid="photo-file-input"
                      />
                    </label>
                    {photoFile && (
                      <button
                        type="button"
                        onClick={() => { setPhotoFile(null); setPhotoPreview(''); }}
                        className="text-xs text-rose-700 font-semibold hover:underline"
                        data-testid="photo-clear-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {photoError && <p className="text-sm text-rose-700 mt-2" data-testid="photo-error">{photoError}</p>}
                </div>

                {/* Resume */}
                <div className="mt-6">
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-2">
                    Resume <span className="text-slate-400 font-normal">(PDF or DOCX, up to 5&nbsp;MB)</span>
                  </label>
                {!resumeFile ? (
                  <label
                    htmlFor="resume-upload"
                    className="block border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
                    data-testid="resume-dropzone"
                  >
                    <Upload className="w-10 h-10 text-blue-700 mx-auto" />
                    <p className="mt-3 font-bold text-slate-900">Click to upload your resume</p>
                    <p className="text-xs text-slate-500 mt-1">PDF or DOCX · max 5 MB</p>
                    <input
                      id="resume-upload"
                      data-testid="resume-file-input"
                      type="file"
                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleResumePick}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="mt-6 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl flex items-center justify-between" data-testid="resume-picked">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-9 h-9 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{resumeFile.name}</p>
                        <p className="text-xs text-slate-500">{Math.round(resumeFile.size / 1024)} KB</p>
                      </div>
                    </div>
                    <button onClick={() => setResumeFile(null)} className="p-2 hover:bg-white rounded-lg" data-testid="resume-clear-btn">
                      <X className="w-5 h-5 text-slate-600" />
                    </button>
                  </div>
                )}
                {resumeError && (
                  <p className="text-sm text-rose-700 mt-2" data-testid="resume-error">{resumeError}</p>
                )}
                </div>

                <div className="mt-10 flex justify-between">
                  <button onClick={() => setStep(2)} className="px-4 py-2.5 text-slate-700 hover:bg-slate-100 rounded-lg font-semibold flex items-center gap-2" data-testid="signup-back-step-3">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid="signup-submit-btn"
                    className="px-7 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 disabled:opacity-60 text-white font-bold rounded-lg shadow-lg flex items-center gap-2"
                  >
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <>Create my account <CheckCircle2 className="w-4 h-4" /></>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          By creating an account you agree to our{' '}
          <Link to="/terms" className="underline hover:text-slate-700">Terms</Link> and{' '}
          <Link to="/privacy" className="underline hover:text-slate-700">Privacy Policy</Link>.
        </p>
      </div>

      {/* Local Tailwind helper class */}
      <style>{`
        .input-base {
          width: 100%;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 15px;
          background: #fff;
          transition: border-color .15s ease;
        }
        .input-base:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
        }
      `}</style>
    </JobsShell>
  );
}

function Field({ label, hint, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
